import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { InfoIcon } from "lucide-react";
import type { AttendanceRecordDTO } from "../../domain/repo/list-attendance-records";

type StudentAttendanceRecordsTableProps = {
  data: AttendanceRecordDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
};

export function StudentAttendanceRecordsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: StudentAttendanceRecordsTableProps) {
  const columns: TanstackReactTable.ColumnDef<AttendanceRecordDTO>[] = [
    createSortableColumn("checkedInAt", "ວັນທີ & ເວລາ", {
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">
            {formatDate(row.original.checkedInAt)}
          </div>
          <div className="text-muted-foreground text-xs">
            {formatDateTime(row.original.checkedInAt)}
          </div>
        </div>
      ),
    }),
    {
      header: "ນັກຮຽນ",
      accessorKey: "studentName",
      cell: ({ row }) => (
        <Link
          to="/app/students/$id"
          params={{ id: row.original.studentId }}
          className="font-medium text-primary hover:underline"
        >
          {row.original.studentName}
        </Link>
      ),
    },
    {
      header: "ປະເພດ",
      accessorKey: "sessionType",
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.sessionType === "general_class"
            ? "ຄລາດສ໌ທົ່ວໄປ"
            : "ຮຽນສ່ວນຕົວ"}
        </Badge>
      ),
    },
    {
      header: "ຄລາດສ໌ / ການຈອງ",
      accessorKey: "className",
      cell: ({ row }) => {
        const r = row.original;
        const isPrivateLesson = r.sessionType === "private_lesson";
        if (isPrivateLesson) {
          return (
            <div>
              {r.privateLessonDate && (
                <div className="font-semibold">
                  {formatDate(r.privateLessonDate)}
                </div>
              )}
              {r.privateLessonStartTime && r.privateLessonEndTime && (
                <div className="text-muted-foreground text-xs">
                  {r.privateLessonStartTime} - {r.privateLessonEndTime}
                </div>
              )}
              {!r.privateLessonDate && !r.privateLessonStartTime && "-"}
            </div>
          );
        }
        return (
          <Link
            to="/app/classes/sessions/$id"
            params={{ id: r.classSessionId ?? "" }}
            className="font-semibold text-primary hover:underline"
          >
            {r.className ?? "-"}
          </Link>
        );
      },
    },
    {
      header: "ສິດທີ່ໃຊ້",
      accessorKey: "packageName",
      cell: ({ row }) => {
        const pkg =
          row.original.packageName ?? row.original.privateLessonPackageName;
        return pkg ? <span className="text-destructive">{pkg}</span> : "-";
      },
    },
    {
      header: "ຕັດສິດ",
      accessorKey: "consumed",
      cell: ({ row }) => row.original.consumed,
    },
    {
      header: "Override",
      accessorKey: "hasOverride",
      cell: ({ row }) =>
        row.original.hasOverride ? (
          <div className="flex items-center gap-1">
            <InfoIcon className="size-4 text-destructive" />
            {row.original.overrideReason && (
              <span
                className="max-w-[120px] truncate text-muted-foreground text-xs"
                title={row.original.overrideReason}
              >
                {row.original.overrideReason}
              </span>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      header: "ຜູ້ກວດຊື່",
      accessorKey: "checkedInByName",
      cell: ({ row }) => row.original.checkedInByName ?? "-",
    },
  ];

  return (
    <DataTable<AttendanceRecordDTO, unknown>
      noDataMessage="ບໍ່ພົບບັນທຶກການເຂົ້າຮຽນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
